import { useState, useCallback } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { getUserGymId } from '@/utils/gymAuth';
import { useTranslation } from 'react-i18next';
import { classesService } from '@/services/classesService';
import { scheduleClassesService } from '@/services/scheduleClassesService';
import { useUserStore } from '@/store/userStore';
import { useScheduleSupabaseStore } from '@/store/api/scheduleSupabaseStore';


export const useWeeklySchedule = (isAdmin: boolean, classes: any[], refreshClasses?: () => Promise<void>) => {
  const { t } = useTranslation(['schedule']);
  const queryClient = useQueryClient();
  const { updateScheduledClass } = useScheduleSupabaseStore();

  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [selectedClassDetails, setSelectedClassDetails] = useState<any>(null);
  const [selectedClassDate, setSelectedClassDate] = useState<Date>(new Date());
  const [isBookingDialogOpen, setIsBookingDialogOpen] = useState(false);
  const [selectedBookingClass, setSelectedBookingClass] = useState<any>(null);
  const [selectedBookingSession, setSelectedBookingSession] = useState<any>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editClassInfo, setEditClassInfo] = useState<any>(null);
  const [editClassDate, setEditClassDate] = useState<Date>(new Date());

  const getClassForTimeSlot = useCallback((date: Date, time: string) => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    return classes.find(c => c.class_date === formattedDate && c.time === time);
  }, [classes]);

  const getClassesForTimeSlot = useCallback((date: Date, time: string) => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    return classes.filter(c => c.class_date === formattedDate && c.time === time);
  }, [classes]);

  const handleClassClick = useCallback((classInfo: any, date: Date) => {
    if (isAdmin) {
      setSelectedClassDetails(classInfo);
      setSelectedClassDate(date);
      setIsDetailsDialogOpen(true);
    } else {
      setSelectedBookingClass(classInfo);
      setSelectedBookingSession({ date });
      setIsBookingDialogOpen(true);
    }
  }, [isAdmin]);

  const handleCapacityUpdate = useCallback(async (classId: string, newCapacity: number) => {
    if (!isAdmin) return;

    const gymId = getUserGymId();
    if (!gymId) {
      toast.error('Gym not selected');
      return;
    }

    try {
      const success = await classesService.updateClassCapacity(gymId, classId, newCapacity);

      if (!success) {
        toast.error('Failed to update class capacity');
        return;
      }

      // Mise à jour locale immédiate de la modale ouverte
      setSelectedClassDetails((prev: any) =>
        prev && prev.id === classId ? { ...prev, capacity: newCapacity } : prev
      );

      // Invalider tous les caches pour propager la nouvelle capacité partout
      const { user, gymId: currentGymId } = useUserStore.getState();
      if (user?.id && currentGymId) {
        scheduleClassesService.invalidateCache(user.id, currentGymId);
        await queryClient.invalidateQueries({ queryKey: ['schedule-classes', user.id, currentGymId] });
        await queryClient.refetchQueries({
          queryKey: ['schedule-classes', user.id, currentGymId],
          type: 'active',
        });
      }

      if (refreshClasses) {
        await refreshClasses();
      }

      toast.success('Class capacity updated successfully');
    } catch (error) {
      console.error('Error in handleCapacityUpdate:', error);
      toast.error('An error occurred while updating the class capacity');
    }
  }, [isAdmin, queryClient, refreshClasses]);


  /**
   * Supprime un cours planifié dans scheduled_classes.
   * @param classId - l'identifiant du cours à supprimer
   * @returns boolean - true si succès, false sinon
   */
  const handleDeleteClass = useCallback(async (classId: string): Promise<boolean> => {
    if (!isAdmin) {
      toast.error(t('errors.deleteFailed'));
      return false;
    }
    
    const gymId = getUserGymId();
    if (!gymId) {
      toast.error(t('errors.noGymSelected'));
      return false;
    }
    
    if (!classId) {
      toast.error(t('errors.deleteFailed'));
      return false;
    }

    try {
      const success = await classesService.deleteClass(gymId, classId);

      if (!success) {
        toast.error(t('errors.deleteFailed'));
        return false;
      }

      // Nettoyage UI
      setIsDetailsDialogOpen(false);
      setSelectedClassDetails(null);

      toast.success(t('success.classDeleted'));
      
      // Invalider le cache avant le refresh pour forcer un fetch frais
      const { user, gymId: currentGymId } = useUserStore.getState();
      if (user?.id && currentGymId) {
        scheduleClassesService.invalidateCache(user.id, currentGymId);
      }
      
      // Refresh schedule after successful deletion
      if (refreshClasses) {
        await refreshClasses();
      }
      
      return true;
    } catch (e: any) {
      console.error('Delete class error:', e);
      
      // Gestion spéciale des erreurs 404 - classe déjà supprimée
      const errorMessage = e?.message || '';
      const isNotFoundError = 
        errorMessage.includes('not found') || 
        errorMessage.includes('Class not found') ||
        errorMessage.includes('404');
      
      if (isNotFoundError) {
        console.log('[handleDeleteClass] Class already deleted, cleaning up UI');
        
        toast.info(t('info.classAlreadyDeleted'));
        
        // Invalider le cache pour nettoyer les données périmées
        const { user: u, gymId: gId } = useUserStore.getState();
        if (u?.id && gId) {
          scheduleClassesService.invalidateCache(u.id, gId);
        }
        
        // Refresh schedule to clean up UI
        if (refreshClasses) {
          await refreshClasses();
        }
        
        // Close dialog since class is gone
        setIsDetailsDialogOpen(false);
        setSelectedClassDetails(null);
        
        return true; // Objectif atteint: la classe n'existe plus
      }
      
      // Pour toutes les autres erreurs, échec réel
      toast.error(t('errors.deleteFailed'));
      return false;
    }
  }, [isAdmin, refreshClasses, t]);

  const handleOpenEditDialog = useCallback((classInfo: any, date: Date) => {
    setIsDetailsDialogOpen(false);
    setEditClassInfo(classInfo);
    setEditClassDate(date);
    setIsEditDialogOpen(true);
  }, []);

  const handleSaveEditedClass = useCallback(async (
    values: { id?: string; name: string; instructor: string; categoryId: string; capacity: number; duration: string; time: string; courseId?: string },
    date: Date
  ): Promise<boolean> => {
    const classId = values.id;
    if (!classId) return false;
    const gymId = getUserGymId();
    if (!gymId) return false;
    const updateData = {
      name: values.name,
      class_date: format(date, 'yyyy-MM-dd'),
      time: values.time,
      instructor: values.instructor,
      category_id: values.categoryId,
      capacity: values.capacity,
      duration: values.duration,
      course_id: values.courseId,
    };
    const success = await updateScheduledClass(classId, updateData);
    if (success && refreshClasses) {
      await refreshClasses();
    }
    return success;
  }, [updateScheduledClass, refreshClasses]);

  return {
    // Dialog state
    isDetailsDialogOpen,
    setIsDetailsDialogOpen,
    selectedClassDetails,
    selectedClassDate,
    isBookingDialogOpen,
    setIsBookingDialogOpen,
    selectedBookingClass,
    selectedBookingSession,
    isEditDialogOpen,
    setIsEditDialogOpen,
    editClassInfo,
    editClassDate,

    // Helpers
    getClassForTimeSlot,
    getClassesForTimeSlot,

    // Events
    handleClassClick,
    handleCapacityUpdate,
    handleDeleteClass,
    handleOpenEditDialog,
    handleSaveEditedClass,
    refreshClasses
  };
};
